'use client'

import { memo } from 'react'
import Link from 'next/link'
import { motion, useReducedMotion } from 'framer-motion'
import { Landmark, Lock, ShieldCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { NinxlScrollReveal } from './ninxl-scroll-reveal'

export const NINX_GOVTS_SALES_EMAIL = 'govts@nin.in'

const TERMINAL_LINES = [
  '> MINISTRY://NINX/GOVTS. SESSION OPEN',
  '> CITIZEN DATA CLASSIFICATION: ACTIVE',
  '> EGRESS POLICY: BLOCKED',
  '> QUEUE: 3 CASES PROCESSING LOCALLY',
] as const

const SERVICE_WINDOWS = [
  { label: 'Records', active: true },
  { label: 'Permits', active: false },
  { label: 'Benefits', active: true },
  { label: 'Courts', active: false },
] as const

const HERO_STAMPS = [
  { value: 'ZERO', label: 'foreign cloud egress' },
  { value: '100%', label: 'audit trail coverage' },
  { value: 'AIR-GAP', label: 'ministry ready' },
] as const

/** Pixel capitol + live service counter: civic hero visual */
const NinxlGovtsServiceCounter = memo(function NinxlGovtsServiceCounter() {
  const shouldReduce = useReducedMotion()

  return (
    <div className="ninxl-govts-counter ninxl-pixel-border relative mx-auto w-full max-w-md p-4 md:p-5">
      <div className="ninxl-8bit-label ninxl-govts-counter-title mb-3 flex items-center justify-between">
        <span>CITIZEN SERVICE COUNTER</span>
        <span className="ninxl-govts-live-dot" aria-hidden />
      </div>

      <svg viewBox="0 0 320 200" className="mb-4 h-auto w-full" aria-hidden>
        <rect x="0" y="0" width="320" height="200" fill="var(--ninxl-theme-panel, #fff)" fillOpacity="0.04" />
        {/* Pixel capitol base */}
        <rect x="40" y="130" width="240" height="56" fill="#21728d" fillOpacity="0.12" stroke="#21728d" strokeWidth="2" />
        <rect x="72" y="96" width="176" height="36" fill="#21728d" fillOpacity="0.18" stroke="#21728d" strokeWidth="2" />
        <rect x="108" y="62" width="104" height="36" fill="#134b5f" fillOpacity="0.22" stroke="#21728d" strokeWidth="2" />
        <rect x="138" y="34" width="44" height="30" fill="#21728d" fillOpacity="0.35" stroke="#21728d" strokeWidth="2" />
        {/* Columns */}
        {[88, 128, 168, 208].map((x) => (
          <rect key={x} x={x} y="112" width="12" height="18" fill="#88a1ac" fillOpacity="0.45" />
        ))}
        {/* Department windows */}
        {SERVICE_WINDOWS.map((win, index) => {
          const x = 56 + index * 64
          return (
            <g key={win.label}>
              <rect
                x={x}
                y="142"
                width="48"
                height="28"
                fill={win.active ? '#21728d' : 'transparent'}
                fillOpacity={win.active ? 0.28 : 0}
                stroke="#21728d"
                strokeWidth="1.5"
                className={win.active && !shouldReduce ? 'ninxl-govts-window-pulse' : undefined}
                style={{ animationDelay: `${index * 0.4}s` }}
              />
              <text x={x + 24} y="160" textAnchor="middle" className="fill-[#134b5f] text-[8px] font-bold">
                {win.label}
              </text>
            </g>
          )
        })}
        {/* Perimeter ring */}
        <rect
          x="16"
          y="16"
          width="288"
          height="168"
          fill="none"
          stroke="#21728d"
          strokeWidth="1.5"
          strokeDasharray="6 5"
          strokeOpacity="0.35"
        />
        <text x="160" y="28" textAnchor="middle" className="fill-[#5f7380] text-[8px] font-bold tracking-[0.14em]">
          NATIONAL DATA PERIMETER
        </text>
      </svg>

      <div className="space-y-2">
        {['CASE-2041 · Benefits eligibility', 'CASE-2042 · Permit verification', 'CASE-2043 · Grievance intake'].map(
          (ticket, index) => (
            <motion.div
              key={ticket}
              className="ninxl-govts-queue-row flex items-center justify-between gap-2 px-2 py-1.5"
              initial={shouldReduce ? false : { opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + index * 0.15, duration: 0.45 }}
            >
              <span className="ninxl-8bit-label text-[10px]">{ticket}</span>
              <span className="ninxl-govts-status-pill">LOCAL</span>
            </motion.div>
          ),
        )}
      </div>
    </div>
  )
})

/** Asymmetric govts hero: terminal manifest left, service counter right */
export const NinxlGovtsHero = memo(function NinxlGovtsHero({ className }: { className?: string }) {
  const shouldReduce = useReducedMotion()

  return (
    <section data-section="govts-hero" className={cn('relative pt-12 pb-14 md:pt-16 md:pb-20', className)}>
      <div className="mx-auto grid max-w-6xl items-center gap-10 px-6 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">
        <NinxlScrollReveal variant="left">
          <div className="ninxl-govts-terminal ninxl-pixel-border mb-6 p-4 md:p-5">
            <div className="ninxl-8bit-label mb-3 flex items-center gap-2">
              <Landmark className="h-3.5 w-3.5" aria-hidden />
              PUBLIC SECTOR TERMINAL v1.0
            </div>
            <div className="space-y-1.5 font-mono text-sm md:text-base">
              {TERMINAL_LINES.map((line, index) => (
                <motion.div
                  key={line}
                  className="ninxl-govts-terminal-line"
                  initial={shouldReduce ? false : { opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.18, duration: 0.35 }}
                >
                  {line}
                </motion.div>
              ))}
            </div>
          </div>

          <p className="ninxl-8bit-label ninxl-theme-text-accent mb-3 tracking-[0.18em]">SOVEREIGN PUBLIC AI</p>
          <h1 className="ninxl-8bit-heading ninxl-theme-text mb-5 text-left text-xl leading-tight sm:text-2xl md:text-3xl">
            AI that serves citizens
            <br />
            <span className="ninxl-theme-text-muted">without leaving the republic.</span>
          </h1>

          <p className="ninxl-8bit-body mb-8 max-w-xl text-left text-base md:text-lg">
            NINX Govts runs inside your national perimeter, processing cases, drafting responses, and
            auditing every action on infrastructure you control.
          </p>

          <div className="mb-8 flex flex-wrap gap-3">
            <Link
              href={`mailto:${NINX_GOVTS_SALES_EMAIL}?subject=NINX%20Govts%20Briefing%20Request`}
              className="ninxl-pixel-btn ninxl-pixel-btn-primary"
            >
              Request Briefing
            </Link>
            <a href="#govts-scorecard" className="ninxl-pixel-btn">
              View scorecard
            </a>
          </div>

          <div className="flex flex-wrap gap-3">
            {HERO_STAMPS.map((stamp) => (
              <div key={stamp.label} className="ninxl-govts-stamp px-3 py-2 text-center">
                <div className="ninxl-theme-text-accent text-sm font-bold md:text-base">{stamp.value}</div>
                <div className="ninxl-8bit-label mt-0.5 text-[8px] md:text-[9px]">{stamp.label}</div>
              </div>
            ))}
          </div>
        </NinxlScrollReveal>

        <NinxlScrollReveal variant="right" delay={0.1}>
          <div className="relative">
            <div className="ninxl-govts-hero-badge absolute -left-2 -top-2 z-10 hidden items-center gap-1.5 px-2 py-1 md:flex">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
              <span className="ninxl-8bit-label text-[9px]">CLASSIFIED: OFFICIAL</span>
            </div>
            <div className="ninxl-govts-hero-badge absolute -bottom-2 -right-2 z-10 flex items-center gap-1.5 px-2 py-1">
              <Lock className="h-3.5 w-3.5" aria-hidden />
              <span className="ninxl-8bit-label text-[9px]">ZERO EGRESS</span>
            </div>
            <NinxlGovtsServiceCounter />
          </div>
        </NinxlScrollReveal>
      </div>
    </section>
  )
})